import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import ScheduleIcon from '@mui/icons-material/Schedule';
import StarIcon from '@mui/icons-material/Star';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import InsightsIcon from '@mui/icons-material/Insights';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { navigate } from '../../redux/navigator/action';
import { APP_ROUTES, ROUTES } from '../../types/global/routes.types';
import {
    ensureObsidianDashboardFonts,
    OBSIDIAN,
    OBSIDIAN_BODY_FONT,
    OBSIDIAN_DISPLAY_FONT,
    dashboardShellSx,
} from '../../constants/obsidianDashboardTheme';
import { CashOutModal } from '../../components/CashOutModal';
import { SandboxStudioEarningsCard } from '../../components/Sandbox/SandboxStudioEarningsCard';
import { SandboxPaymentHistoryPanel } from '../../components/Sandbox/SandboxPaymentHistoryPanel';
import { SandboxStudioCourseCard } from '../../components/Sandbox/SandboxStudioCourseCard';
import { SandboxLaunchpadTile, SandboxMetricCard } from '../../components/Sandbox/SandboxStudioWidgets';
import { SandboxEditCourseModal } from '../../components/Sandbox/SandboxEditCourseModal';
import { SandboxCreateCourseModal } from '../../components/Sandbox/SandboxCreateCourseModal';
import { SandboxManageClassesModal } from '../../components/Sandbox/SandboxManageClassesModal';
import { SandboxManageClassAssetsModal } from '../../components/Sandbox/SandboxManageClassAssetsModal';
import {
    SandboxReviewSubmissionModal,
    type SandboxPendingSubmission,
} from '../../components/Sandbox/SandboxReviewSubmissionModal';
import { SandboxOfficeHoursModal } from '../../components/Sandbox/SandboxOfficeHoursModal';
import { resolveSandboxMediaUrl, type SandboxCourse } from '../../utils/sandboxCourseApi';
import {
    fetchTeacherDashboard,
    fetchTeacherPendingReviews,
    publishSandboxCourse,
    reviewSandboxAssignment,
} from '../../redux/sandbox/actions';
import { showToastMessage, useDispatchPromise } from '../../utils/AppUtils';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

const SURFACE = '#0e0e0e';

type StudioStats = {
    total_students?: number;
    pending_reviews?: number;
    gross_earnings_cents?: number;
    course_earnings_cents?: number;
    office_hours_earnings_cents?: number;
    pending_earnings_cents?: number;
    office_hours_hold_days?: number;
    available_to_withdraw_cents?: number;
    watch_hours?: number;
    rating_avg?: number;
};

export function SandboxStudioPage() {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [courses, setCourses] = useState<SandboxCourse[]>([]);
    const [stats, setStats] = useState<StudioStats>({});
    const [reviews, setReviews] = useState<SandboxPendingSubmission[]>([]);
    const [cashOutOpen, setCashOutOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [manageCourseId, setManageCourseId] = useState<string | null>(null);
    const [assetsCourse, setAssetsCourse] = useState<SandboxCourse | null>(null);
    const [editCourse, setEditCourse] = useState<SandboxCourse | null>(null);
    const [reviewing, setReviewing] = useState<SandboxPendingSubmission | null>(null);
    const [reviewBusy, setReviewBusy] = useState(false);
    const [officeHoursOpen, setOfficeHoursOpen] = useState(false);

    useEffect(() => {
        ensureObsidianDashboardFonts();
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [dash, pending] = (await Promise.all([
                dispatchPromise(fetchTeacherDashboard()),
                dispatchPromise(fetchTeacherPendingReviews()),
            ])) as [any, any];
            const dashData = dash?.data || dash;
            setCourses(dashData?.courses || []);
            setStats(dashData?.stats || {});
            setReviews(pending?.data?.submissions || pending?.submissions || []);
        } catch (e: any) {
            showToastMessage(e?.message || 'Could not load studio', 'error');
        } finally {
            setLoading(false);
        }
    }, [dispatchPromise]);

    useEffect(() => {
        load();
    }, [load]);

    // Deep-link: /sandbox/studio?manage=<courseId> opens Add Class modal
    useEffect(() => {
        const manageId = searchParams.get('manage');
        if (!manageId || loading) return;
        setManageCourseId(manageId);
        const next = new URLSearchParams(searchParams);
        next.delete('manage');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams, loading]);

    // Google Calendar OAuth return for office hours
    useEffect(() => {
        const oh = searchParams.get('office_hours');
        if (!oh) return;
        if (oh === 'connected') {
            showToastMessage('Google Calendar connected for office hours', 'success');
            setOfficeHoursOpen(true);
        } else if (oh === 'error') {
            showToastMessage(searchParams.get('message') || 'Could not connect Google Calendar', 'error');
        }
        const next = new URLSearchParams(searchParams);
        next.delete('office_hours');
        next.delete('message');
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const handlePublish = async (id: string) => {
        try {
            await dispatchPromise(publishSandboxCourse(id));
            showToastMessage('Course published', 'success');
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Publish failed — add classes with private projects first', 'error');
        }
    };

    const handleReview = async (courseId: string, submissionId: string, pass: boolean, feedback?: string) => {
        setReviewBusy(true);
        try {
            await dispatchPromise(
                reviewSandboxAssignment({
                    courseId,
                    submissionId,
                    body: {
                        status: pass ? 'passed' : 'revision_requested',
                        teacher_review: feedback || (pass ? 'Approved' : 'Please revise and resubmit'),
                        teacher_rating: pass ? 5 : 3,
                    },
                }),
            );
            showToastMessage(pass ? 'Marked passed' : 'Requested revision', 'success');
            setReviewing(null);
            await load();
        } catch (e: any) {
            showToastMessage(e?.message || 'Review failed', 'error');
        } finally {
            setReviewBusy(false);
        }
    };

    const pending = Number(stats.pending_reviews) || 0;
    const activeCount = courses.filter(c => c.status === 'published' || c.status === 'draft').length;

    if (loading) {
        return (
            <Box
                sx={{
                    ...dashboardShellSx,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: SURFACE,
                }}>
                <CircularProgress sx={{ color: OBSIDIAN.primary }} />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                ...dashboardShellSx,
                bgcolor: SURFACE,
                color: OBSIDIAN.onSurface,
                width: '100%',
                pb: 10,
                px: { xs: 2, md: 3, lg: 4 },
            }}>
            {/* Row 1: badge + actions | Row 2: title — matches desktop command-center layout */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1, mb: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.25,
                            py: 0.4,
                            borderRadius: 999,
                            bgcolor: OBSIDIAN.surfaceContainerHighest,
                            width: 'max-content',
                        }}>
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: OBSIDIAN.secondary,
                                animation: 'sandboxPulse 1.6s ease-in-out infinite',
                                '@keyframes sandboxPulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.35 },
                                },
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.1em',
                                textTransform: 'uppercase',
                                color: OBSIDIAN.secondary,
                                fontFamily: OBSIDIAN_BODY_FONT,
                            }}>
                            Teacher Command Center
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                        <Button
                            onClick={() => dispatch(navigate({ routes: APP_ROUTES.SANDBOX }))}
                            sx={{
                                color: OBSIDIAN.onSurfaceVariant,
                                textTransform: 'none',
                                fontFamily: OBSIDIAN_BODY_FONT,
                                fontWeight: 600,
                                fontSize: 13,
                                minWidth: 0,
                                px: 1,
                                '&:hover': { color: OBSIDIAN.primary, bgcolor: 'transparent' },
                            }}>
                            Academy
                        </Button>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setCreateOpen(true)}
                            sx={{
                                px: 2,
                                py: 1,
                                borderRadius: 999,
                                background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                                color: OBSIDIAN.onPrimaryFixed,
                                fontFamily: OBSIDIAN_DISPLAY_FONT,
                                fontWeight: 700,
                                fontSize: 12,
                                textTransform: 'none',
                                boxShadow: '0 4px 16px rgba(255,201,101,0.25)',
                                '&:hover': { filter: 'brightness(1.05)' },
                            }}>
                            New Course
                        </Button>
                    </Box>
                </Box>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 800,
                        fontSize: { xs: 24, md: 28 },
                        color: OBSIDIAN.onSurface,
                        letterSpacing: '-0.02em',
                    }}>
                    Sandbox Studio
                </Typography>
            </Box>

            <SandboxStudioEarningsCard
                grossCents={stats.gross_earnings_cents || 0}
                availableCents={stats.available_to_withdraw_cents || 0}
                pendingCents={stats.pending_earnings_cents || 0}
                courseCents={stats.course_earnings_cents || 0}
                officeHoursCents={stats.office_hours_earnings_cents || 0}
                holdDays={stats.office_hours_hold_days || 7}
                onWithdraw={() => setCashOutOpen(true)}
                onPayoutSettings={() => dispatch(navigate({ routes: ROUTES.ACCOUNT_EXTERNAL_ACCOUNT }))}
            />

            <SandboxPaymentHistoryPanel />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                    gap: 1.5,
                    mb: 3,
                }}>
                <SandboxMetricCard
                    label="Total Students"
                    value={String(stats.total_students || 0)}
                    icon={<GroupIcon sx={{ fontSize: 18, color: OBSIDIAN.primary }} />}
                />
                <SandboxMetricCard
                    label="Watch Hours"
                    value={`${stats.watch_hours || 0}h`}
                    sub="total"
                    icon={<ScheduleIcon sx={{ fontSize: 18, color: OBSIDIAN.primary }} />}
                />
                <SandboxMetricCard
                    label="Student Rating"
                    value={stats.rating_avg ? String(stats.rating_avg) : '—'}
                    sub={stats.rating_avg ? 'from enrolled students' : 'no ratings yet'}
                    subColor={stats.rating_avg ? OBSIDIAN.primary : undefined}
                    icon={<StarIcon sx={{ fontSize: 18, color: OBSIDIAN.primary }} />}
                />
                <SandboxMetricCard
                    label="Project Reviews"
                    value={pending ? `${pending} Pending` : '0 Pending'}
                    sub={pending ? 'action needed' : 'all clear'}
                    valueColor={pending ? '#ff7351' : OBSIDIAN.onSurface}
                    badge={pending || undefined}
                    icon={<AssignmentTurnedInIcon sx={{ fontSize: 18, color: OBSIDIAN.primary }} />}
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 13,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: OBSIDIAN.onSurfaceVariant,
                        }}>
                        Studio Launchpad
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: OBSIDIAN.primary, fontFamily: OBSIDIAN_BODY_FONT }}>
                        Shortcut Tools
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.25 }}>
                    <SandboxLaunchpadTile
                        icon={<VideoCallIcon sx={{ fontSize: 20 }} />}
                        iconColor={OBSIDIAN.primary}
                        title="Upload Class"
                        subtitle="Add to a course"
                        onClick={() => (courses[0] ? setManageCourseId(courses[0]._id) : setCreateOpen(true))}
                    />
                    <SandboxLaunchpadTile
                        icon={<ScheduleIcon sx={{ fontSize: 20 }} />}
                        iconColor={OBSIDIAN.secondary}
                        title="Office hours"
                        subtitle="Meet · free or paid"
                        onClick={() => setOfficeHoursOpen(true)}
                    />
                    <SandboxLaunchpadTile
                        icon={<AssignmentTurnedInIcon sx={{ fontSize: 20 }} />}
                        iconColor="#ff7351"
                        title="Grade Submissions"
                        subtitle={pending ? `${pending} Waiting` : 'None waiting'}
                        onClick={() => {
                            if (!pending) showToastMessage('No pending reviews', 'info');
                            else
                                document
                                    .getElementById('sandbox-pending-reviews')
                                    ?.scrollIntoView({ behavior: 'smooth' });
                        }}
                    />
                    <SandboxLaunchpadTile
                        icon={<InsightsIcon sx={{ fontSize: 20 }} />}
                        iconColor={OBSIDIAN.primary}
                        title="Student Analytics"
                        subtitle="Cohort Retention"
                        onClick={() => showToastMessage('Analytics coming soon', 'info')}
                    />
                </Box>
            </Box>

            {pending > 0 && (
                <Box id="sandbox-pending-reviews" sx={{ mb: 3 }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 13,
                            color: OBSIDIAN.onSurfaceVariant,
                            mb: 1.5,
                            textTransform: 'uppercase',
                        }}>
                        Pending Reviews
                    </Typography>
                    {reviews.slice(0, 8).map(sub => {
                        const project = sub.project && typeof sub.project === 'object' ? sub.project : null;
                        const thumb =
                            resolveSandboxMediaUrl((project as any)?.feature_image || (project as any)?.thumbnail) ||
                            undefined;
                        return (
                            <Box
                                key={sub._id}
                                sx={{
                                    p: 1.5,
                                    mb: 1,
                                    borderRadius: 2,
                                    bgcolor: OBSIDIAN.surfaceContainer,
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'stretch',
                                }}>
                                <Box
                                    onClick={() => setReviewing(sub)}
                                    sx={{
                                        width: 96,
                                        flexShrink: 0,
                                        borderRadius: 1.5,
                                        bgcolor: '#000',
                                        backgroundImage: thumb ? `url(${thumb})` : undefined,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        minHeight: 64,
                                    }}>
                                    <PlayCircleOutlineIcon sx={{ color: OBSIDIAN.primary, fontSize: 28 }} />
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            color: OBSIDIAN.onSurface,
                                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                                        }}>
                                        {sub.user?.userName || sub.user?.firstName || 'Student'} ·{' '}
                                        {typeof sub.course === 'object' ? sub.course?.title : ''}
                                    </Typography>
                                    {sub.class_title ? (
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: OBSIDIAN.secondary,
                                                fontFamily: OBSIDIAN_BODY_FONT,
                                            }}>
                                            {sub.class_title}
                                        </Typography>
                                    ) : null}
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: OBSIDIAN.onSurfaceVariant,
                                            mb: 1,
                                            fontFamily: OBSIDIAN_BODY_FONT,
                                        }}
                                        noWrap>
                                        {(project as any)?.title ? `${(project as any).title} · ` : ''}
                                        {sub.notes || 'No notes'}
                                    </Typography>
                                    <Button
                                        size="small"
                                        startIcon={<PlayCircleOutlineIcon />}
                                        onClick={() => setReviewing(sub)}
                                        sx={{
                                            color: OBSIDIAN.primary,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            px: 0,
                                            minWidth: 0,
                                        }}>
                                        Watch & review
                                    </Button>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_DISPLAY_FONT,
                        fontWeight: 700,
                        fontSize: 13,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: OBSIDIAN.onSurfaceVariant,
                    }}>
                    Courses & Classes
                </Typography>
                <Typography
                    sx={{ fontSize: 12, color: OBSIDIAN.primary, fontWeight: 600, fontFamily: OBSIDIAN_BODY_FONT }}>
                    {activeCount} Active
                </Typography>
            </Box>

            {courses.length === 0 ? (
                <Box sx={{ p: 3, borderRadius: 3, bgcolor: OBSIDIAN.surfaceContainer, textAlign: 'center', mb: 3 }}>
                    <Typography sx={{ color: OBSIDIAN.onSurfaceVariant, fontFamily: OBSIDIAN_BODY_FONT, mb: 2 }}>
                        No courses yet. Create a course, then add classes under it.
                    </Typography>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                        sx={{
                            borderRadius: 999,
                            background: `linear-gradient(90deg, ${OBSIDIAN.primary}, #feb700)`,
                            color: OBSIDIAN.onPrimaryFixed,
                            textTransform: 'none',
                            fontWeight: 700,
                        }}>
                        New Course
                    </Button>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    {courses.map(c => (
                        <SandboxStudioCourseCard
                            key={c._id}
                            course={c}
                            onOpenCourse={() =>
                                dispatch(
                                    navigate({
                                        routes: `${APP_ROUTES.SANDBOX_COURSE.replace(':courseId', c._id)}?from=studio`,
                                    }),
                                )
                            }
                            onEdit={() => setEditCourse(c)}
                            onAddClass={() => setManageCourseId(c._id)}
                            onManageAssets={() => setAssetsCourse(c)}
                            onPublish={() => handlePublish(c._id)}
                        />
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    borderRadius: 3,
                    bgcolor: OBSIDIAN.surfaceContainerHigh,
                    p: 2,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'flex-start',
                    mb: 2,
                }}>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,201,101,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: OBSIDIAN.primary,
                        flexShrink: 0,
                        mt: 0.25,
                    }}>
                    <LightbulbIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_DISPLAY_FONT,
                            fontWeight: 700,
                            fontSize: 12,
                            color: OBSIDIAN.onSurface,
                        }}>
                        Teacher Tip of the Day
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: 11,
                            color: OBSIDIAN.onSurfaceVariant,
                            mt: 0.5,
                            lineHeight: 1.55,
                        }}>
                        Sandbox video critiques drive 3x higher retention than text comments. Grade pending project
                        reviews using 60-second video reaction recordings.
                    </Typography>
                </Box>
            </Box>

            <SandboxCreateCourseModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={({ courseId, openClasses }) => {
                    load();
                    if (openClasses) setManageCourseId(courseId);
                }}
            />
            <SandboxEditCourseModal
                open={Boolean(editCourse)}
                course={editCourse}
                onClose={() => setEditCourse(null)}
                onSaved={load}
            />
            <SandboxManageClassesModal
                open={Boolean(manageCourseId)}
                courseId={manageCourseId}
                onClose={() => setManageCourseId(null)}
                onSaved={load}
            />
            <SandboxManageClassAssetsModal
                open={Boolean(assetsCourse)}
                courseId={assetsCourse?._id || ''}
                courseTitle={assetsCourse?.title}
                onClose={() => {
                    setAssetsCourse(null);
                    void load();
                }}
            />
            <SandboxReviewSubmissionModal
                open={Boolean(reviewing)}
                submission={reviewing}
                busy={reviewBusy}
                onClose={() => {
                    if (!reviewBusy) setReviewing(null);
                }}
                onPass={feedback => {
                    if (!reviewing) return;
                    const courseId = typeof reviewing.course === 'object' ? reviewing.course?._id : reviewing.course;
                    if (!courseId) return;
                    return handleReview(String(courseId), reviewing._id, true, feedback);
                }}
                onRequestRevision={feedback => {
                    if (!reviewing) return;
                    const courseId = typeof reviewing.course === 'object' ? reviewing.course?._id : reviewing.course;
                    if (!courseId) return;
                    return handleReview(String(courseId), reviewing._id, false, feedback);
                }}
            />
            <SandboxOfficeHoursModal open={officeHoursOpen} onClose={() => setOfficeHoursOpen(false)} />
            <CashOutModal open={cashOutOpen} onClose={() => setCashOutOpen(false)} sourceWallet="sandbox" />
        </Box>
    );
}
